import React from 'react'
import { Provider } from 'react-redux'
import { isRouteErrorResponse, Outlet, RouterProvider, useRouteError } from 'react-router-dom'

import '@core/api/observability/initialize'

import { enableMapSet } from 'immer'

import ExportItemsRoot from '@containers/export-items/export-items-root'
import ProtectedRoute, { Progress } from '@containers/main/main-route-protected'
import { MaintenanceWarning } from '@containers/main/maintenance-warning'
import { UserSessionWarning } from '@containers/main/user-session-warning'
import { ApiProvider, BffClientProvider } from '@core/api'
import AuthorizedApolloProvider from '@core/api/apollo'
import ObservabilityProvider from '@core/api/observability'
import Header from '@core/components/Header'
import { ScrollBarContainer } from '@core/components/ScrollBar'
import { routeMap } from '@core/constants/routes'
import { HEADER_HEIGHT } from '@core/constants/styles'
import ProjectsRoot from '@core/projects/projects-root'
import { Snacks } from '@core/snack/snack'
import { useStore } from '@core/store'
import { AppTheme } from '@core/styles/theme'

import Auth0Provider from './providers/auth0'

enableMapSet()

const ProviderRedux = (props: { children: React.ReactNode }) => {
  const store = useStore()

  return <Provider store={store}>{props.children}</Provider>
}

export const App = () => {
  return (
    <Auth0Provider>
      <BffClientProvider>
        <AuthorizedApolloProvider>
          <ApiProvider>
            <ProviderRedux>
              <AppTheme>
                <Snacks />
                <Outlet />
              </AppTheme>
            </ProviderRedux>
          </ApiProvider>
        </AuthorizedApolloProvider>
      </BffClientProvider>
    </Auth0Provider>
  )
}

const ErrorPage = React.lazy(() => import('@pages/404'))

const router = [
  {
    path: '/',
    element: <App />,
    ErrorBoundary: () => {
      const error = useRouteError()

      return isRouteErrorResponse(error) ? (
        <AppTheme>
          <React.Suspense fallback={<Progress />}>
            <ErrorPage />
          </React.Suspense>
        </AppTheme>
      ) : (
        // @ts-ignore
        <h4>{error?.message || error}</h4>
      )
    },
    children: [
      {
        index: true,
        lazy: () => import('@pages/auth/login-page'),
      },
      {
        path: routeMap.LOGIN_SSO_ROUTE,
        lazy: () => import('@pages/auth/login-page'),
      },
      {
        element: (
          <ProtectedRoute>
            <Header />
            <UserSessionWarning />
            <MaintenanceWarning />
            <ExportItemsRoot />
            <ScrollBarContainer
              id="scrollbar"
              style={{ height: `calc(100vh - ${HEADER_HEIGHT}px)` }}
            >
              <Outlet />
            </ScrollBarContainer>

            <ProjectsRoot />
          </ProtectedRoute>
        ),
        children: [
          {
            path: routeMap.AUTHOR_ITEM_SET,
            lazy: () => import('@pages/item-set/item-set-page'),
          },
          {
            path: routeMap.USERS_ROUTE,
            lazy: () => import('@pages/users/users-page'),
          },
          {
            path: routeMap.AUTHOR_ROUTE,
            lazy: () => import('@pages/author/author-page'),
          },
          {
            path: routeMap.AUTHOR_V2_LANDING_PAGE,
            lazy: () => import('@pages/author-v2/landing-page/author-v2-landing-page'),
          },
          {
            path: routeMap.AUTHOR_V2_CREATE,
            lazy: () => import('@pages/author-v2/create/author-create-page'),
          },
          {
            path: routeMap.AUTHOR_V2_RECENT_WORK,
            lazy: () => import('@pages/author-v2/recent-work/author-recent-work-page'),
          },
          {
            path: routeMap.AUTHOR_V2_ITEM_ROUTE,
            lazy: () => import('@pages/author-v2/batch/batch-page'),
          },
          {
            path: routeMap.AUTHOR_V2_ERROR,
            lazy: () => import('@pages/author-v2/batch/error-page/batch-error-page'),
          },
          {
            path: routeMap.AUTHOR_ITEM_ROUTE,
            lazy: () => import('@pages/author/author-page'),
          },
          {
            path: routeMap.AUTHOR_CLOZE_ROUTE,
            lazy: () => import('@pages/author-cloze/author-cloze-page'),
          },
          {
            path: routeMap.MY_FOLDERS_ROUTE,
            lazy: () => import('@pages/my-folders/my-folders-page'),
          },
          {
            path: routeMap.MY_FOLDER_ROUTE,
            lazy: () => import('@pages/my-folders/my-folders-page'),
          },
          {
            path: routeMap.MY_FOLDER_ITEM_ROUTE,
            lazy: () => import('@pages/my-folders/my-folders-page'),
          },
          {
            path: routeMap.MY_FOLDERS_DRAFT_ITEMS,
            lazy: () => import('@pages/my-folders/my-folders-draft-page'),
          },
          {
            path: routeMap.MY_FOLDERS_DRAFT_ITEM_ROUTE,
            lazy: () => import('@pages/my-folders/my-folders-draft-page'),
          },
        ],
      },
    ],
  },
]

const AppRouter = () => {
  return <RouterProvider router={ObservabilityProvider.createRouter(router)} />
}

export default AppRouter
